import { useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '../../api/http'
import type { FileEntry } from '../../api/types'
import { IconAttach, IconSend, IconStop } from '../icons'
import { Button } from '../ui/Button'
import { useToast } from '../ui/Toast'
import { MAX_ATTACHMENTS, isPastedImage, pastedFileName, planAttachmentAdd } from './attachments'

export interface ComposerProps {
  running: boolean
  disabled?: boolean
  onSend(text: string, attachments: { path: string }[]): Promise<boolean>
  onStop(): void
  /** Attachments are uploaded into this workspace (the chat's; personal when omitted). */
  workspaceId?: string
  modelSlot?: React.ReactNode
  /** Extra control shown next to the model picker (e.g. the chat's knowledge-base scope). */
  kbSlot?: React.ReactNode
  /** Control shown first in the picker row (e.g. the repository picker on the new-chat screen). */
  startSlot?: React.ReactNode
  /** Control shown in the action area, before send/stop (e.g. the workspace picker on the new-chat screen). */
  endSlot?: React.ReactNode
  /** When true, omits the outer rounded card border/bg so it integrates into a parent card. */
  bare?: boolean
}

/**
 * The composer is one card: a textarea, then a control bar. The bar is one row — the attach button
 * on the left, the pickers in the middle, the action on the right — so the pickers can never push
 * the action off the card, and both the pickers and the action can shrink or truncate instead of
 * overflowing.
 *
 * On phones the pickers stay on the same row with the attach button and action button,
 * shrinking and truncating gracefully (icon-only KB scope picker, truncated model name).
 * Wrapping is avoided so the control bar stays compact and unified across viewports.
 */
export function Composer({ running, disabled, onSend, onStop, workspaceId, modelSlot, kbSlot, startSlot, endSlot, bare }: ComposerProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const [text, setText] = useState('')
  const [files, setFiles] = useState<FileEntry[]>([])
  const [uploading, setUploading] = useState(false)
  const [sending, setSending] = useState(false)
  const [dragging, setDragging] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const area = useRef<HTMLTextAreaElement>(null)
  // The ref is authoritative so a drop/paste landing mid-upload sees the real count.
  const picked = useRef<FileEntry[]>([])
  const dragDepth = useRef(0)

  const commit = (next: FileEntry[]) => {
    picked.current = next
    setFiles(next)
  }

  const submit = async () => {
    if ((!text.trim() && files.length === 0) || sending) return
    setSending(true)
    const ok = await onSend(text, files.map(f => ({ path: f.path })))
    setSending(false)
    if (ok) {
      setText('')
      commit([])
      area.current?.focus()
    }
  }

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      void submit()
    }
  }

  /** Shared by the attach button, drag & drop and paste: uploads what fits, reports what does not. */
  const addFiles = async (list: FileList | File[] | null) => {
    const incoming = list ? Array.from(list) : []
    if (!incoming.length) return
    const { accepted, dropped } = planAttachmentAdd(picked.current.length, incoming.length)
    if (dropped > 0) toast(t('chat.attachLimit', { max: MAX_ATTACHMENTS, files: dropped }), 'error')
    if (accepted === 0) return
    setUploading(true)
    try {
      for (const f of incoming.slice(0, accepted)) {
        const form = new FormData()
        form.append('file', f, f.name)
        const entry = await api.post<FileEntry>(`/api/files/upload?path=uploads${workspaceId ? `&workspace=${encodeURIComponent(workspaceId)}` : ''}`, form)
        commit([...picked.current, entry])
      }
    } catch (err) {
      toast((err as Error).message, 'error')
    } finally {
      setUploading(false)
      if (input.current) input.current.value = ''
    }
  }

  const carriesFiles = (e: DragEvent) => Array.from(e.dataTransfer.types).includes('Files')

  const onDragEnter = (e: DragEvent) => {
    if (disabled || !carriesFiles(e)) return
    e.preventDefault()
    dragDepth.current += 1
    setDragging(true)
  }

  const onDragOver = (e: DragEvent) => {
    if (disabled || !carriesFiles(e)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
  }

  const onDragLeave = () => {
    // Some browsers report an empty `types` on dragleave, so do not gate this on `carriesFiles`.
    dragDepth.current = Math.max(0, dragDepth.current - 1)
    if (dragDepth.current === 0) setDragging(false)
  }

  const onDrop = (e: DragEvent) => {
    if (disabled || !carriesFiles(e)) return
    e.preventDefault()
    dragDepth.current = 0
    setDragging(false)
    void addFiles(e.dataTransfer.files)
  }

  /** Ctrl+V pastes images only; anything else falls through to the textarea. */
  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (disabled) return
    const images = Array.from(e.clipboardData?.items ?? [])
      .filter(i => i.kind === 'file' && isPastedImage(i.type))
      .map(i => i.getAsFile())
      .filter((f): f is File => f !== null)
    if (!images.length) return
    e.preventDefault()
    const stamp = Date.now()
    void addFiles(images.map((f, i) => new File([f], pastedFileName(f.type, stamp + i), { type: f.type })))
  }

  const canSend = !disabled && (text.trim().length > 0 || files.length > 0)

  return (
    <div
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`relative p-2 ${bare ? '' : `rounded-2xl border bg-cream-50 transition-shadow focus-within:border-ginger-500 focus-within:ring-[3px] focus-within:ring-ginger-100 ${dragging ? 'border-ginger-500 ring-[3px] ring-ginger-100' : 'border-cream-300'}`}`}
    >
      {dragging && (
        <div className={`pointer-events-none absolute inset-0 z-10 flex items-center justify-center border-2 border-dashed border-ginger-500 bg-cream-50/90 text-sm font-medium text-ginger-600 ${bare ? 'rounded-b-2xl' : 'rounded-2xl'}`}>
          {t('chat.dropFiles')}
        </div>
      )}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-2 pt-1">
          {files.map(f => (
            <span key={f.path} className="inline-flex items-center gap-1 rounded-[7px] border border-line bg-cream-100 px-2 py-1 text-xs text-ink-300">
              {f.name}
              <button aria-label={t('common.remove')} className="text-ink-500 hover:text-ink-100" onClick={() => commit(picked.current.filter(x => x.path !== f.path))}>×</button>
            </span>
          ))}
        </div>
      )}
      <textarea
        ref={area}
        rows={Math.min(8, Math.max(2, text.split('\n').length))}
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={onKey}
        onPaste={onPaste}
        disabled={disabled}
        placeholder={t('chat.placeholder')}
        aria-label={t('chat.placeholder')}
        className="w-full resize-none bg-transparent px-2 py-2 text-[15px] leading-6 placeholder:text-ink-600 focus:outline-none focus-visible:outline-none max-sm:text-base"
      />
      <div className="flex items-center gap-1.5 px-1">
        <input ref={input} type="file" multiple hidden onChange={e => void addFiles(e.target.files)} />
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('chat.attach')}
          title={t('chat.attach')}
          loading={uploading}
          disabled={disabled}
          onClick={() => input.current?.click()}
          className="shrink-0 text-ink-400 hover:bg-cream-100 hover:text-ink-100"
        >
          {!uploading && <IconAttach width={16} height={16} />}
        </Button>
        {/* Pickers: compact inline items, shrinkable so they never overflow. */}
        <div className="flex min-w-0 flex-1 items-center gap-1 sm:gap-1.5">
          {startSlot}
          {kbSlot}
          {modelSlot && <div className="min-w-0 max-w-full">{modelSlot}</div>}
        </div>
        {/* Desktop-only hint: Enter sends, Shift+Enter starts a new line (the phones hide it). */}
        <span className="hidden shrink-0 items-center gap-1 pr-1 text-[11px] text-ink-500 sm:inline-flex">
          <kbd className="rounded-[5px] border border-cream-300 bg-cream-100 px-1.5 py-0.5 font-sans text-[10.5px] font-medium text-ink-400">{t('chat.enterKey')}</kbd>
          {t('chat.enterToSend')}
        </span>
        {/* Action button: always right-aligned and shrink-0 */}
        <div className="flex shrink-0 items-center gap-1.5 ml-auto">
          {endSlot}
          {running ? (
            <Button
              variant="danger"
              size="icon-sm"
              aria-label={t('chat.stop')}
              title={t('chat.stop')}
              onClick={onStop}
              className="rounded-full"
            >
              <IconStop width={16} height={16} />
            </Button>
          ) : (
            <Button
              size="icon-sm"
              aria-label={t('chat.send')}
              title={t('chat.send')}
              loading={sending}
              disabled={!canSend}
              onClick={() => void submit()}
              className="!h-8 !w-8 rounded-full disabled:bg-cream-300 disabled:text-ink-400 disabled:opacity-100"
            >
              {!sending && <IconSend width={16} height={16} />}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
